/** Utilidades de bytes. Nada aqui e criptografia — so manipulacao de buffers. */
import { MalformedFrameError } from './errors.js'
import { sodium } from './sodium.js'

/**
 * Codifica texto ASCII sem depender de `TextEncoder`.
 *
 * O crypto-core roda em Node, no Worker e no JSI do React Native. Depender de
 * um global de plataforma numa biblioteca de protocolo e pedir para quebrar
 * em um dos tres. Os rotulos de dominio sao ASCII por construcao.
 */
export function ascii(text: string): Uint8Array {
  const out = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code > 0x7f) throw new RangeError(`rotulo nao-ASCII: ${text}`)
    out[i] = code
  }
  return out
}

export function concat(...parts: readonly Uint8Array[]): Uint8Array {
  let total = 0
  for (const p of parts) total += p.length
  const out = new Uint8Array(total)
  let offset = 0
  for (const p of parts) {
    out.set(p, offset)
    offset += p.length
  }
  return out
}

/**
 * Comparacao em tempo constante. Nunca use `===` nem loop com saida
 * antecipada para comparar material secreto — o tempo vaza o conteudo.
 */
export function equalConstantTime(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  return sodium().memcmp(a, b)
}

/** Ordem lexicografica. Usada para dar o mesmo codigo de seguranca aos dois lados. */
export function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length)
  for (let i = 0; i < n; i++) {
    const av = a[i] ?? 0
    const bv = b[i] ?? 0
    if (av !== bv) return av < bv ? -1 : 1
  }
  return a.length - b.length
}

export function writeUint16BE(value: number, into: Uint8Array, offset: number): void {
  into[offset] = (value >>> 8) & 0xff
  into[offset + 1] = value & 0xff
}

export function readUint16BE(from: Uint8Array, offset: number): number {
  return ((from[offset] ?? 0) << 8) | (from[offset + 1] ?? 0)
}

export function writeUint32BE(value: number, into: Uint8Array, offset: number): void {
  into[offset] = (value >>> 24) & 0xff
  into[offset + 1] = (value >>> 16) & 0xff
  into[offset + 2] = (value >>> 8) & 0xff
  into[offset + 3] = value & 0xff
}

export function readUint32BE(from: Uint8Array, offset: number): number {
  return (
    ((from[offset] ?? 0) * 0x1000000 +
      ((from[offset + 1] ?? 0) << 16) +
      ((from[offset + 2] ?? 0) << 8) +
      (from[offset + 3] ?? 0)) >>>
    0
  )
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

/** base64url sem padding — usado nos fingerprints e no corpo das requisicoes. */
export function toBase64Url(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0
    const b1 = bytes[i + 1]
    const b2 = bytes[i + 2]
    out += B64URL[b0 >> 2]
    out += B64URL[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)]
    if (b1 === undefined) break
    out += B64URL[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)]
    if (b2 === undefined) break
    out += B64URL[b2 & 0x3f]
  }
  return out
}

export function fromBase64Url(text: string): Uint8Array {
  const clean = text.trim()
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4))
  let acc = 0
  let bits = 0
  let pos = 0
  for (const ch of clean) {
    const v = B64URL.indexOf(ch)
    if (v < 0) throw new MalformedFrameError(`caractere invalido em base64url: ${ch}`)
    acc = (acc << 6) | v
    bits += 6
    if (bits >= 8) {
      bits -= 8
      out[pos++] = (acc >> bits) & 0xff
    }
  }
  return out.subarray(0, pos)
}

/** Apaga o conteudo do buffer na memoria. Chame ao descartar chave. */
export function wipe(...buffers: readonly Uint8Array[]): void {
  const s = sodium()
  for (const b of buffers) s.memzero(b)
}
